/**
 * LA LUMIERE QUI TRAVERSE LE FAISCEAU EST UNE AFFIRMATION, PAS UNE DECORATION.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LE DEFAUT QUE CE MODULE CORRIGE (demande produit, 2026-09-26).
 *
 * Les cards de synchro animaient un degrade le long de chaque faisceau, sur
 * TOUTES les connexions, quel que soit leur etat. Une connexion en
 * « Reconnexion requise » ne transporte pourtant AUCUNE donnee : son jeton
 * OAuth est mort, le cron ne collecte plus rien. Y faire courir une lumiere
 * revient a affirmer un flux qui n'existe pas.
 *
 * Ce n'est pas un defaut cosmetique. L'ecran sert precisement a repondre a
 * « mes trades remontent-ils ? », et l'animation est la premiere chose que
 * l'oeil capte, bien avant le badge textuel du pied de card. Une card morte
 * qui pulse comme une card vivante ment plus fort que le badge ne rectifie.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ON ETEINT LA LUMIERE, ON N'EFFACE PAS LE TRAIT.
 *
 * Le trait au repos (`pathColor` / `pathOpacity`) subsiste : le lien OAuth
 * EXISTE, la prop firm est bien rattachee a Edgely, et c'est une information
 * vraie qu'il faut garder. Seul le degrade anime disparait. La card dit alors
 * « le lien est la, rien ne coule » -- ce qui est exactement l'etat du monde.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LA REGLE EST DERIVEE DU `tone` DEJA CALCULE, ET N'EN RECALCULE AUCUN.
 *
 * `resolveSyncPresentation` est la seule autorite sur l'etat d'une connexion
 * (cf. son en-tete : elle compose `displayStatus` et `realtimeStatus`). Ce
 * module ne relit ni l'un ni l'autre : il ne fait que traduire son verdict
 * visuel en un booleen d'animation. Dupliquer la detection ici, c'est se
 * garantir que les deux divergeront un jour -- un badge ambre au-dessus d'un
 * faisceau qui coule.
 *
 * MODULE PUR : aucun import `wasp/*` ni React, meme convention que
 * `tradovateSyncPresentation.ts`.
 */

import type { SyncTone } from "./tradovateSyncPresentation";

/**
 * La lumiere doit-elle traverser le faisceau ?
 *
 * ── LE MAPPING, ET POURQUOI IL EST CE QU'IL EST ────────────────────────────
 *
 * `positive`  → LUMIERE. Seul cas ou la collecte fonctionne vraiment
 *               (`active` / `syncing`, socket vivant ou REST nominal). C'est
 *               la seule configuration ou animer un flux dit la verite.
 *
 * `warning`   → ETEINT. Recouvre `needs_reauth` (le cas de la demande),
 *               `delayed`, et la synchro degradee par un socket interrompu.
 *               Le cas limite est `statusSyncedDelayed`, ou le REST prend bien
 *               le relais : on eteint quand meme, parce que la lumiere qui
 *               COURT est une metaphore de temps reel, pas de « quelque chose
 *               finira par arriver dans 15 minutes ». Le badge ambre porte
 *               deja la nuance en toutes lettres ; l'animation, elle, ne sait
 *               dire que oui ou non, et le « non » est le moins trompeur des
 *               deux.
 *
 * `critical`  → ETEINT. `error` : plus rien ne remonte, aucune ambiguite.
 *
 * `neutral`   → ETEINT, et c'est le choix qui demandait le plus d'examen.
 *               Deux etats le produisent aujourd'hui :
 *
 *                 `idle`                → la connexion est en veille, elle ne
 *                                         collecte pas. Rien ne coule.
 *                 `active_no_accounts`  → la connexion vit, mais ZERO compte
 *                                         n'est synchronise. Le lien OAuth est
 *                                         sain ; seulement, il ne transporte
 *                                         aucun trade, puisqu'il n'y a aucun
 *                                         compte au bout.
 *
 *               Le second est le seul cas discutable : on pourrait plaider que
 *               la connexion « marche ». Mais la card rend justement ce cas
 *               avec un cote droit VIDE (« aucun compte synchronise ») : une
 *               lumiere partant du hub vers rien du tout serait l'image la plus
 *               contradictoire des quatre. Et `resolveSyncPresentation` a deja
 *               tranche dans le meme sens en refusant de peindre cet etat en
 *               teal, pour ne pas inviter a ne rien faire.
 *
 *               Regle generale qui en decoule : on n'allume QUE sur une preuve
 *               positive de bon fonctionnement. Un ton neutre est par
 *               definition une absence de preuve.
 *
 * ── POURQUOI UNE LISTE EXPLICITE ET NON `tone !== "positive"` ──────────────
 *
 * Le resultat serait identique AUJOURD'HUI, mais la propriete recherchee n'est
 * pas la valeur : c'est qu'un futur `SyncTone` ajoute a l'union ne puisse pas
 * se retrouver anime par defaut. Avec un `Record` exhaustif, TypeScript refuse
 * de compiler tant que le nouveau ton n'a pas recu une decision explicite. Un
 * `!==` compilerait sans bruit et allumerait la lumiere sur un etat que
 * personne n'a examine -- exactement le mode de defaillance silencieux qu'on
 * corrige.
 */
const BEAM_LIGHT_BY_TONE: Record<SyncTone, boolean> = {
  positive: true,
  warning: false,
  critical: false,
  neutral: false,
};

/**
 * Le faisceau doit-il porter une lumiere en mouvement ?
 *
 * `true` = la connexion collecte reellement ; `false` = le trait au repos
 * subsiste seul.
 */
export function shouldAnimateBeam(tone: SyncTone): boolean {
  return BEAM_LIGHT_BY_TONE[tone] ?? false;
}

/**
 * Couleurs a passer a `AnimatedBeam` pour eteindre le degrade anime.
 *
 * ⚠️ ON NE TOUCHE PAS A `animated-beam.tsx` : ce composant est PARTAGE avec la
 * landing page, ou l'animation est permanente et voulue. On agit par ses props.
 *
 * `transparent` est un mot-cle de couleur CSS valide en valeur de `stop-color`
 * SVG, equivalent a `rgba(0,0,0,0)` : les quatre arrets du degrade tombent a
 * une alpha nulle, et le second trace (`stroke="url(#id)"`) ne peint plus rien.
 * Le `strokeOpacity="1"` du composant multiplie une alpha deja nulle, il ne la
 * restaure pas. Le PREMIER trace, celui du repos, ne lit aucune de ces deux
 * props : il reste donc intact, ce qui est tout l'objet de la manoeuvre.
 */
export const BEAM_GRADIENT_OFF = "transparent";
